import { useEffect, useMemo, useRef, useState } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import {
  Archive,
  Check,
  EllipsisVertical,
  Flag,
  Inbox,
  ListFilter,
  Mail,
  MailOpen,
  ShieldAlert,
  Paperclip,
  RefreshCw,
  Search,
  Trash2,
} from 'lucide-react';
import {
  Avatar,
  Button,
  Dropdown,
  DropdownContent,
  DropdownItem,
  DropdownLabel,
  DropdownSeparator,
  DropdownTrigger,
  EmptyState,
  Tooltip,
  cn,
} from '@waymail/ui';
import { useUi, type Density, type QuickFilter } from '../store/ui';
import {
  contarFiltros,
  filtrosEfetivos,
  type FiltrosDaLista,
  type Periodo,
} from '../lib/filtrosDaLista';
import { dateGroup, listTime, type DateGroup } from '../lib/date';
import type { AcaoEmMassa } from '../lib/consultas';
import { useTriagem } from '../lib/triagem';
import type { ConversaDaApi } from '../lib/api';
import { ehTeclaDeMenu, pontoDoElemento, pontoDoEvento, type PontoDoMenu } from './MenuDeContexto';
import { MenuDaMensagem } from './MenuDaMensagem';

const GROUP_HEIGHT = 26;

/** Compacta esconde a previa; as outras duas mostram duas linhas. */
const ALTURA_DA_LINHA: Record<Density, number> = {
  compact: 68,
  comfortable: 112,
  relaxed: 128,
};

/** Itens marcaveis do menu "Filtros"; somam-se a aba ativa. */
const FILTROS_MARCAVEIS: [Exclude<keyof FiltrosDaLista, 'periodo'>, string][] = [
  ['naoLidas', 'Não lidas'],
  ['sinalizadas', 'Sinalizadas'],
  ['comAnexo', 'Com anexo'],
  ['semResposta', 'Sem resposta'],
];
const PERIODOS: [Periodo, string][] = [
  ['hoje', 'Hoje'],
  ['7d', 'Últimos 7 dias'],
  ['30d', 'Últimos 30 dias'],
];

type Row = { kind: 'group'; group: DateGroup } | { kind: 'thread'; conversa: ConversaDaApi };

export interface MessageListProps {
  conversas: ConversaDaApi[];
  carregando: boolean;
  temMais: boolean;
  carregarMais: () => void;
  onListRef?: (scrollToId: (id: string) => void) => void;
  /**
   * A caixa ainda nao completou o primeiro sync (ADR 0056): lista vazia ai nao
   * e "nada por aqui". `erro` quando a ultima tentativa falhou.
   */
  primeiroSync?: 'andamento' | 'erro' | null;
  /** A consulta da lista falhou e nao ha nada em cache: erro com nova tentativa, nunca "Nada por aqui". */
  falhou?: boolean;
  tentarDeNovo?: () => void;
}

export function MessageList({
  conversas,
  carregando,
  temMais,
  carregarMais,
  onListRef,
  primeiroSync = null,
  falhou = false,
  tentarDeNovo,
}: MessageListProps) {
  const density = useUi((s) => s.density);
  const filter = useUi((s) => s.filter);
  const setFilter = useUi((s) => s.setFilter);
  const query = useUi((s) => s.query);
  const setQuery = useUi((s) => s.setQuery);
  const mensagemId = useUi((s) => s.mensagemId);
  const selectMensagem = useUi((s) => s.selectMensagem);
  const checkedIds = useUi((s) => s.checkedIds);
  const toggleChecked = useUi((s) => s.toggleChecked);
  const setChecked = useUi((s) => s.setChecked);
  const clearChecked = useUi((s) => s.clearChecked);

  const filtrosMarcados = useUi((s) => s.filtros);
  const alternarFiltro = useUi((s) => s.alternarFiltro);
  const setPeriodo = useUi((s) => s.setPeriodo);
  const limparFiltros = useUi((s) => s.limparFiltros);

  const triagem = useTriagem();
  const filtros = filtrosEfetivos(filtrosMarcados, filter);
  const ativos = contarFiltros(filtros);
  const temSelecao = checkedIds.length > 0;
  const [menu, setMenu] = useState<{ conversa: ConversaDaApi; ponto: PontoDoMenu } | null>(null);
  const secaoRef = useRef<HTMLElement>(null);
  const todasRef = useRef<HTMLInputElement>(null);

  /**
   * Age sobre a selecao e a limpa so quando o servidor aceita: manter marcado
   * o que saiu da lista deixa a contagem mentindo, e limpar antes de saber
   * tira de quem errou a chance de tentar de novo. O comando IMAP vai na fila
   * do outbox (Secao 10.4); o toast traz Desfazer e a falha parcial.
   */
  function emMassa(action: AcaoEmMassa) {
    void triagem.executar(checkedIds, action, { aoConcluir: clearChecked });
  }

  const rowHeight = ALTURA_DA_LINHA[density];
  const scrollRef = useRef<HTMLDivElement>(null);
  const ultimoClique = useRef<number | null>(null);
  const [grupoVisivel, setGrupoVisivel] = useState<DateGroup | null>(null);
  const [busca, setBusca] = useState(query);

  // A busca vai ao servidor: sem espera, cada tecla vira uma consulta.
  useEffect(() => {
    const timer = setTimeout(() => setQuery(busca), 350);
    return () => clearTimeout(timer);
  }, [busca, setQuery]);

  const rows = useMemo<Row[]>(() => {
    const saida: Row[] = [];
    let atual: DateGroup | null = null;
    for (const conversa of conversas) {
      const grupo = dateGroup(conversa.lastMessageAt);
      if (grupo !== atual) {
        atual = grupo;
        saida.push({ kind: 'group', group: grupo });
      }
      saida.push({ kind: 'thread', conversa });
    }
    return saida;
  }, [conversas]);

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: (index) => (rows[index]?.kind === 'group' ? GROUP_HEIGHT : rowHeight),
    overscan: 12,
  });

  useEffect(() => {
    if (!onListRef) return;
    onListRef((id: string) => {
      const index = rows.findIndex((r) => r.kind === 'thread' && r.conversa.messageId === id);
      if (index >= 0) virtualizer.scrollToIndex(index, { align: 'auto' });
    });
  }, [onListRef, rows, virtualizer]);

  const itensVisiveis = virtualizer.getVirtualItems();

  useEffect(() => {
    const primeiro = itensVisiveis[0];
    if (!primeiro) {
      setGrupoVisivel(null);
      return;
    }
    const row = rows[primeiro.index];
    if (!row) return;
    setGrupoVisivel(row.kind === 'group' ? row.group : dateGroup(row.conversa.lastMessageAt));
  }, [itensVisiveis, rows]);

  // Rolagem infinita: pre-carrega a 20 itens do fim (Secao 6.4).
  useEffect(() => {
    const ultimo = itensVisiveis[itensVisiveis.length - 1];
    if (ultimo === undefined || !temMais) return;
    if (ultimo.index >= rows.length - 20) carregarMais();
  }, [itensVisiveis, rows.length, temMais, carregarMais]);

  const todas = useMemo(() => conversas.map((c) => c.messageId), [conversas]);
  const todasMarcadas = todas.length > 0 && todas.every((id) => checkedIds.includes(id));

  // "Algumas": o estado do meio so existe por propriedade, nao por atributo.
  useEffect(() => {
    if (todasRef.current !== null) {
      todasRef.current.indeterminate = temSelecao && !todasMarcadas;
    }
  }, [temSelecao, todasMarcadas]);

  // Shift+F10 e a tecla de menu abrem o menu da conversa aberta na lista.
  const pedidoDeMenu = useUi((s) => s.pedidoDeMenu);
  const pedidoVisto = useRef(pedidoDeMenu);
  // Atalho `v`: o mesmo menu, aberto pela conversa selecionada.
  useEffect(() => {
    if (pedidoDeMenu === pedidoVisto.current) return;
    pedidoVisto.current = pedidoDeMenu;
    const conversa = conversas.find((c) => c.messageId === mensagemId);
    if (conversa === undefined) return;
    const linha = document.getElementById(`row-${mensagemId}`);
    // Em tela estreita a leitura cobre a lista: o menu abre no alto, sem ancora.
    setMenu({ conversa, ponto: linha ? pontoDoElemento(linha) : { x: 24, y: 96 } });
  }, [pedidoDeMenu, conversas, mensagemId]);

  useEffect(() => {
    function aoTeclar(e: KeyboardEvent) {
      if (!ehTeclaDeMenu(e) || menu !== null || mensagemId === null) return;
      const alvo = e.target instanceof HTMLElement ? e.target : null;
      if (alvo === null) return;
      if (alvo.closest('input, textarea, select, [contenteditable="true"]') !== null) return;
      // Foco em outra parte (barra de pastas, botoes) tem o menu dela.
      if (alvo !== document.body && !(secaoRef.current?.contains(alvo) ?? false)) return;
      const conversa = conversas.find((c) => c.messageId === mensagemId);
      const linha = document.getElementById(`row-${mensagemId}`);
      if (conversa === undefined || linha === null) return;
      e.preventDefault();
      setMenu({ conversa, ponto: pontoDoElemento(linha) });
    }
    window.addEventListener('keydown', aoTeclar);
    return () => window.removeEventListener('keydown', aoTeclar);
  }, [conversas, mensagemId, menu]);

  function marcarIntervalo(index: number) {
    const inicio = Math.min(ultimoClique.current ?? index, index);
    const fim = Math.max(ultimoClique.current ?? index, index);
    setChecked(
      rows
        .slice(inicio, fim + 1)
        .filter((r): r is { kind: 'thread'; conversa: ConversaDaApi } => r.kind === 'thread')
        .map((r) => r.conversa.messageId),
    );
  }

  /** A caixinha da linha: com Shift marca o intervalo, sem Shift alterna uma. */
  function aoMarcar(conversa: ConversaDaApi, index: number, event: React.MouseEvent) {
    if (event.shiftKey && ultimoClique.current !== null) marcarIntervalo(index);
    else toggleChecked(conversa.messageId);
    ultimoClique.current = index;
  }

  function aoClicar(conversa: ConversaDaApi, index: number, event: React.MouseEvent) {
    if (event.shiftKey && ultimoClique.current !== null) {
      marcarIntervalo(index);
      return;
    }
    if (event.metaKey || event.ctrlKey) {
      toggleChecked(conversa.messageId);
      ultimoClique.current = index;
      return;
    }
    ultimoClique.current = index;
    clearChecked();
    selectMensagem(conversa.messageId);
  }

  return (
    <section
      ref={secaoRef}
      aria-label="Lista de mensagens"
      className="flex w-full min-w-0 flex-col bg-app"
    >
      <div className="px-4 pt-4 pb-3">
        <div className="flex h-12 items-center gap-3 rounded-xl bg-sunken pr-4 pl-1.5">
          <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-rail">
            <Search size={18} strokeWidth={2} aria-hidden="true" className="text-white" />
          </span>
          <input
            type="search"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            /**
             * Esc devolve o foco para a lista. Sem isto, quem entrava na busca
             * pela tecla `/` ficava preso: os atalhos de navegacao nao valem
             * dentro de campo de texto, e nada mais tirava o cursor de la sem
             * mouse. Achado 12 da campanha de verificacao.
             */
            onKeyDown={(e) => {
              if (e.key === 'Escape') e.currentTarget.blur();
            }}
            placeholder="Buscar"
            aria-label="Buscar em todas as contas"
            className="h-full min-w-0 flex-1 bg-transparent text-[14px] text-primary placeholder:text-tertiary focus:outline-none"
          />
          <kbd className="hidden shrink-0 font-mono text-[11px] text-tertiary sm:block">Ctrl K</kbd>
        </div>
      </div>

      {/*
        A barra e a mesma com e sem selecao - o layout do print - mas sem
        mensagem marcada os botoes nao tem alvo e ficam desabilitados. Antes
        eles apareciam sempre ativos e nao faziam nada: pareciam as acoes
        principais da tela e eram decoracao.
      */}
      <div
        role="toolbar"
        aria-label="Ações sobre as conversas selecionadas"
        className="flex items-center gap-1 px-4 pb-3"
      >
        <label className="mr-auto flex min-w-0 cursor-pointer items-center gap-2 text-[13px] toque:min-h-10">
          <input
            ref={todasRef}
            type="checkbox"
            checked={todasMarcadas}
            disabled={todas.length === 0}
            onChange={() => (todasMarcadas ? clearChecked() : setChecked(todas))}
            aria-label={`Selecionar as ${todas.length} conversas carregadas`}
            className="size-4 toque:size-6 cursor-pointer accent-[var(--color-rail)]"
          />
          <span
            aria-live="polite"
            data-contagem=""
            className={cn(
              'whitespace-nowrap',
              temSelecao ? 'font-semibold text-accent' : 'text-tertiary',
            )}
          >
            {temSelecao
              ? `${checkedIds.length} ${checkedIds.length === 1 ? 'selecionada' : 'selecionadas'}`
              : 'Selecionar tudo'}
          </span>
        </label>

        <button
          type="button"
          disabled={!temSelecao}
          onClick={() => emMassa('delete')}
          className={cn(
            'flex shrink-0 items-center gap-1.5 rounded-lg bg-badge-suave px-2.5 py-1.5 text-[12px] font-semibold text-badge transition-colors toque:min-h-10 toque:min-w-10 toque:justify-center',
            temSelecao ? 'cursor-pointer hover:brightness-95' : 'cursor-not-allowed opacity-40',
          )}
        >
          <Trash2 size={14} strokeWidth={2} aria-hidden="true" />
          {/* Em tela estreita so o icone: o texto empurrava "N selecionadas" para o corte. */}
          <span className="max-sm:sr-only">Excluir</span>
        </button>

        {BOTOES.filter((b) => b.principal).map(({ icon, label, action }) => (
          <BotaoDeAcao
            key={action}
            icon={icon}
            label={label}
            disabled={!temSelecao}
            onClick={() => emMassa(action)}
          />
        ))}

        {/* As acoes secundarias ficam num menu: sem ele a barra quebrava em duas linhas. */}
        <Dropdown>
          <Tooltip content="Mais ações">
            <DropdownTrigger asChild>
              <button
                type="button"
                aria-label="Mais ações"
                disabled={!temSelecao}
                className="flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-lg text-secondary transition-colors hover:bg-hover hover:text-primary disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent toque:size-10"
              >
                <EllipsisVertical size={16} strokeWidth={1.8} aria-hidden="true" />
              </button>
            </DropdownTrigger>
          </Tooltip>
          <DropdownContent align="end">
            {BOTOES.filter((b) => !b.principal).map(({ icon: Icon, label, action }) => (
              <DropdownItem key={action} onSelect={() => emMassa(action)}>
                <Icon size={14} strokeWidth={1.8} aria-hidden="true" />
                {label}
              </DropdownItem>
            ))}
          </DropdownContent>
        </Dropdown>

        {temSelecao && (
          <button
            type="button"
            onClick={clearChecked}
            className="ml-1 shrink-0 cursor-pointer rounded-lg px-2.5 py-1.5 text-[13px] font-semibold text-secondary hover:bg-hover toque:min-h-10"
          >
            Limpar
          </button>
        )}
      </div>

      <div className="flex items-center gap-5 border-b border-subtle px-4">
        {(
          [
            ['all', 'Prioritários'],
            ['unread', 'Não lidas'],
            // Sem esta aba, mensagem adiada some e nao ha como reve-la antes da
            // hora - dado escondido que o usuario nao alcanca.
            ['snoozed', 'Adiadas'],
          ] as [QuickFilter, string][]
        ).map(([id, rotulo]) => (
          <button
            key={id}
            type="button"
            onClick={() => setFilter(id)}
            aria-pressed={filter === id}
            className={cn(
              'relative cursor-pointer pb-2.5 text-[14px] transition-colors toque:pt-2.5',
              filter === id
                ? 'font-bold text-primary'
                : 'font-medium text-tertiary hover:text-secondary',
            )}
          >
            {rotulo}
            {filter === id && (
              <span
                aria-hidden="true"
                className="absolute inset-x-0 -bottom-px h-0.5 rounded-full bg-rail"
              />
            )}
          </button>
        ))}

        {/* Filtros combinaveis: somam-se a aba e o menu fica aberto ao marcar. */}
        <Dropdown>
          <DropdownTrigger asChild>
            <button
              type="button"
              data-ativo={ativos > 0 ? '' : undefined}
              className={cn(
                'mb-2 ml-auto flex cursor-pointer items-center gap-1.5 rounded-lg px-2.5 py-1 text-[12px] font-semibold hover:bg-hover toque:mb-0 toque:min-h-10',
                ativos > 0 ? 'bg-selected text-accent' : 'bg-sunken text-secondary',
              )}
            >
              <ListFilter size={14} strokeWidth={2} aria-hidden="true" />
              {ativos > 0 ? `Filtros · ${ativos}` : 'Filtros'}
            </button>
          </DropdownTrigger>
          <DropdownContent align="end">
            {FILTROS_MARCAVEIS.filter(
              // A aba (ou a barra lateral) ja impoe este filtro: marcar de novo nao soma nada.
              ([chave]) =>
                !(
                  (chave === 'naoLidas' && filter === 'unread') ||
                  (chave === 'sinalizadas' && filter === 'flagged')
                ),
            ).map(([chave, rotulo]) => (
              <DropdownItem
                key={chave}
                role="menuitemcheckbox"
                aria-checked={filtros[chave]}
                onSelect={(e) => {
                  e.preventDefault();
                  alternarFiltro(chave);
                }}
              >
                <Check
                  size={14}
                  strokeWidth={2}
                  aria-hidden="true"
                  className={filtros[chave] ? '' : 'invisible'}
                />
                {rotulo}
              </DropdownItem>
            ))}
            <DropdownSeparator />
            <DropdownLabel>Período</DropdownLabel>
            {PERIODOS.map(([id, rotulo]) => (
              <DropdownItem
                key={id}
                role="menuitemradio"
                aria-checked={filtros.periodo === id}
                onSelect={(e) => {
                  e.preventDefault();
                  setPeriodo(filtros.periodo === id ? null : id);
                }}
              >
                <Check
                  size={14}
                  strokeWidth={2}
                  aria-hidden="true"
                  className={filtros.periodo === id ? '' : 'invisible'}
                />
                {rotulo}
              </DropdownItem>
            ))}
            {ativos > 0 && (
              <>
                <DropdownSeparator />
                <DropdownItem onSelect={limparFiltros}>Limpar filtros</DropdownItem>
              </>
            )}
          </DropdownContent>
        </Dropdown>
      </div>

      {carregando ? (
        <div className="flex flex-1 items-center justify-center">
          <RefreshCw
            size={20}
            aria-label="Carregando mensagens"
            className="animate-spin text-tertiary motion-reduce:animate-none"
          />
        </div>
      ) : conversas.length === 0 && falhou ? (
        <EmptyState
          icon={ShieldAlert}
          title="Não foi possível carregar as mensagens."
          description="Verifique a conexão e tente de novo."
          action={
            <Button size="sm" variant="ghost" onClick={tentarDeNovo}>
              <RefreshCw size={16} strokeWidth={1.5} aria-hidden="true" />
              Tentar de novo
            </Button>
          }
        />
      ) : conversas.length === 0 && primeiroSync !== null && query.trim() === '' ? (
        <EmptyState
          icon={primeiroSync === 'erro' ? ShieldAlert : RefreshCw}
          title={
            primeiroSync === 'erro'
              ? 'Erro ao sincronizar esta caixa.'
              : 'Sincronizando a primeira vez…'
          }
          description={
            primeiroSync === 'erro'
              ? 'Não foi possível trazer as mensagens. Tentaremos de novo sozinhos; se a senha mudou, reconecte a caixa.'
              : 'As mensagens desta caixa aparecem aqui assim que chegarem.'
          }
        />
      ) : conversas.length === 0 ? (
        <EmptyState
          icon={Inbox}
          title="Nada por aqui."
          description={
            ativos > 0
              ? 'Nenhuma mensagem com esses filtros.'
              : query.trim() !== ''
                ? 'Nenhuma mensagem corresponde à busca.'
                : filter === 'flagged'
                  ? 'Nenhuma mensagem sinalizada.'
                  : filter === 'attachment'
                    ? 'Nenhuma mensagem com anexo.'
                    : 'Quando chegar algo novo, ele aparece nesta lista.'
          }
          action={
            ativos > 0 ? (
              <Button size="sm" variant="ghost" onClick={limparFiltros}>
                Limpar filtros
              </Button>
            ) : undefined
          }
        />
      ) : (
        <div className="relative min-h-0 flex-1">
          {grupoVisivel !== null && (
            <div
              aria-hidden="true"
              className="absolute inset-x-0 top-0 z-10 flex h-6 items-center bg-app/95 px-4 text-[11px] font-bold tracking-[0.06em] text-tertiary uppercase backdrop-blur-[2px]"
            >
              {grupoVisivel}
            </div>
          )}
          <div ref={scrollRef} className="h-full overflow-y-auto px-2 pb-2">
            <ul
              role="listbox"
              aria-label="Conversas"
              aria-activedescendant={mensagemId !== null ? `row-${mensagemId}` : undefined}
              className="relative w-full"
              style={{ height: virtualizer.getTotalSize() }}
            >
              {itensVisiveis.map((item) => {
                const row = rows[item.index];
                if (!row) return null;

                return (
                  <li
                    key={item.key}
                    role="presentation"
                    className="group/li absolute top-0 left-0 w-full"
                    style={{ height: item.size, transform: `translateY(${item.start}px)` }}
                  >
                    {row.kind === 'group' ? (
                      <p className="flex h-full items-center px-2 text-[11px] font-bold tracking-[0.06em] text-tertiary uppercase">
                        {row.group}
                      </p>
                    ) : (
                      <ItemDaLista
                        conversa={row.conversa}
                        selecionada={row.conversa.messageId === mensagemId}
                        marcada={checkedIds.includes(row.conversa.messageId)}
                        emSelecao={temSelecao}
                        compacta={density === 'compact'}
                        onClick={(e) => aoClicar(row.conversa, item.index, e)}
                        onMarcar={(e) => aoMarcar(row.conversa, item.index, e)}
                        onMenu={(ponto) => setMenu({ conversa: row.conversa, ponto })}
                      />
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      )}
      {menu !== null && (
        <MenuDaMensagem
          conversa={menu.conversa}
          ponto={menu.ponto}
          onFechar={() => setMenu(null)}
        />
      )}
    </section>
  );
}

const BOTOES: { icon: typeof Archive; label: string; action: AcaoEmMassa; principal?: boolean }[] =
  [
    { icon: Archive, label: 'Arquivar', action: 'archive', principal: true },
    { icon: MailOpen, label: 'Marcar como lida', action: 'read' },
    { icon: Mail, label: 'Marcar como não lida', action: 'unread' },
    { icon: Flag, label: 'Sinalizar', action: 'flag' },
    { icon: ShieldAlert, label: 'Marcar spam', action: 'spam' },
  ];

function BotaoDeAcao({
  icon: Icon,
  label,
  className,
  onClick,
  disabled = false,
}: {
  icon: typeof Archive;
  label: string;
  className?: string;
  onClick?: () => void;
  disabled?: boolean;
}) {
  return (
    <Tooltip content={label}>
      <button
        type="button"
        aria-label={label}
        disabled={disabled}
        {...(onClick !== undefined ? { onClick } : {})}
        className={cn(
          'flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-lg text-secondary transition-colors hover:bg-hover hover:text-primary toque:size-10',
          'disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent',
          className,
        )}
      >
        <Icon size={16} strokeWidth={1.8} aria-hidden="true" />
      </button>
    </Tooltip>
  );
}

interface ItemProps {
  conversa: ConversaDaApi;
  selecionada: boolean;
  marcada: boolean;
  /** Ha alguma conversa marcada: as caixinhas ficam a vista em todas as linhas. */
  emSelecao: boolean;
  compacta: boolean;
  onClick: (event: React.MouseEvent) => void;
  onMarcar: (event: React.MouseEvent) => void;
  onMenu: (ponto: PontoDoMenu) => void;
}

function ItemDaLista({
  conversa,
  selecionada,
  marcada,
  emSelecao,
  compacta,
  onClick,
  onMarcar,
  onMenu,
}: ItemProps) {
  const remetente = conversa.participants[0] ?? '(sem remetente)';
  const aparece = marcada || emSelecao;

  return (
    <>
      {/* A opcao e a linha; caixinha e menu ficam irmaos dela, nao filhos (nested-interactive). */}
      <div
        role="option"
        id={`row-${conversa.messageId}`}
        aria-selected={selecionada}
        onClick={onClick}
        onContextMenu={(e) => {
          e.preventDefault();
          onMenu(pontoDoEvento(e, e.currentTarget));
        }}
        className={cn(
          'group relative mx-2 flex h-[calc(100%-8px)] cursor-pointer gap-3 rounded-xl px-3 py-2.5 transition-colors duration-[var(--duration-micro)]',
          selecionada ? 'bg-rail text-white' : marcada ? 'bg-selected' : 'hover:bg-sunken',
        )}
      >
        {/*
        Nao lida com tres sinais, como no Outlook: barra, negrito e assunto na
        cor da marca. So o negrito contra semibold, que era o que havia, nao se
        via - a pessoa abria, a mensagem virava lida e parecia que nada mudou.
      */}
        {conversa.unread && !selecionada && (
          <span
            aria-hidden="true"
            className="absolute inset-y-2 left-0 w-[3px] rounded-full bg-rail"
          />
        )}
        <Avatar
          size="sm"
          name={remetente}
          className={cn(
            'mt-0.5 shrink-0 transition-opacity duration-[var(--duration-micro)]',
            // A caixa ocupa o lugar do avatar (como no Outlook e no Gmail), em vez
            // de ficar por cima dele: um quadrado branco sobre o circulo escuro.
            aparece && 'opacity-0',
            !aparece && 'group-hover/li:opacity-0 [@media(hover:none)]:opacity-100',
          )}
        />

        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <div className="flex items-baseline gap-2">
            <span
              className={cn(
                'min-w-0 flex-1 truncate text-[13px]',
                conversa.unread ? 'font-bold' : 'font-normal',
                selecionada ? 'text-white' : 'text-primary',
              )}
            >
              {remetente}
              {conversa.messageCount > 1 && (
                <span
                  className={cn(
                    'ml-1 font-medium',
                    selecionada ? 'text-white/70' : 'text-tertiary',
                  )}
                >
                  ({conversa.messageCount})
                </span>
              )}
            </span>

            <span
              className={cn(
                'shrink-0 text-[11px] font-medium transition-opacity duration-[var(--duration-micro)]',
                selecionada ? 'text-white/80' : 'text-tertiary',
                // Cede o lugar ao menu "..." no hover. No toque a hora fica sempre.
                '[@media(hover:hover)]:group-hover/li:opacity-0',
              )}
            >
              {listTime(conversa.lastMessageAt)}
            </span>
          </div>

          <div className="flex items-center gap-1.5">
            <span
              className={cn(
                'min-w-0 flex-1 truncate text-[13px]',
                conversa.unread ? 'font-bold' : 'font-normal',
                selecionada ? 'text-white' : conversa.unread ? 'text-accent' : 'text-secondary',
              )}
            >
              {conversa.subject}
            </span>

            {conversa.flagged && (
              <Flag
                size={13}
                strokeWidth={1.8}
                aria-label="Sinalizada"
                className={
                  selecionada ? 'fill-white/80 text-white/80' : 'fill-warning text-warning'
                }
              />
            )}
            {conversa.hasAttachment && (
              <Paperclip
                size={13}
                strokeWidth={1.8}
                aria-label="Com anexo"
                className={selecionada ? 'text-white/80' : 'text-tertiary'}
              />
            )}
            {conversa.unread && !selecionada && (
              <span aria-label="Não lida" className="size-1.5 shrink-0 rounded-full bg-rail" />
            )}
          </div>

          {!compacta && conversa.snippet !== null && (
            <p
              className={cn(
                'line-clamp-2 text-[12px] leading-[1.45]',
                selecionada ? 'text-white/75' : 'text-tertiary',
              )}
            >
              {conversa.snippet}
            </p>
          )}
        </div>
      </div>

      {/*
        Fora da linha clicavel, nao dentro: o clique na caixinha nao pode abrir
        a conversa. Ficam fora da ordem de Tab (uma lista de 50 linhas viraria
        100 paradas); o teclado marca com X e abre o menu com Shift+F10, e o
        toque usa o botao de tres pontos, sempre visivel onde nao ha hover. Ficam
        escondidos da arvore de acessibilidade (aria-hidden): uma listbox so pode
        conter opcoes, e o leitor de tela tem os equivalentes - a caixa "Selecionar
        tudo" da barra, a tecla X e o menu pelo Shift+F10.
      */}
      <span
        className={cn(
          // Mesma posicao e tamanho do avatar (32 px, a 20 px da borda da linha).
          'absolute top-[21px] left-[20px] flex size-8 items-center justify-center transition-opacity duration-[var(--duration-micro)]',
          aparece
            ? 'opacity-100'
            : 'pointer-events-none opacity-0 group-hover/li:pointer-events-auto group-hover/li:opacity-100 [@media(hover:none)]:hidden',
        )}
      >
        <input
          type="checkbox"
          checked={marcada}
          tabIndex={-1}
          aria-hidden="true"
          onChange={() => undefined}
          onClick={onMarcar}
          aria-label={`Selecionar ${conversa.subject}`}
          className={cn(
            'peer size-6 cursor-pointer appearance-none rounded-full border-2 bg-raised transition-colors',
            // wc-blue e nao rail: no escuro rail e a cor da linha marcada, e o circulo sumia.
            'border-strong hover:border-wc-blue checked:border-wc-blue checked:bg-wc-blue',
            // O check branco vem do fundo da propria caixa, sem icone sobreposto.
            "checked:bg-[url('data:image/svg+xml,%3Csvg%20xmlns=%22http://www.w3.org/2000/svg%22%20viewBox=%220%200%2016%2016%22%20fill=%22none%22%20stroke=%22white%22%20stroke-width=%222.2%22%20stroke-linecap=%22round%22%20stroke-linejoin=%22round%22%3E%3Cpath%20d=%22M3.5%208.5l3%203%206-7%22/%3E%3C/svg%3E')] checked:bg-center checked:bg-no-repeat checked:bg-[length:14px]",
          )}
        />
      </span>
      <button
        type="button"
        tabIndex={-1}
        aria-hidden="true"
        aria-label={`Mais opções para ${conversa.subject}`}
        onClick={(e) => onMenu(pontoDoElemento(e.currentTarget))}
        className={cn(
          // Ao lado da hora, onde o olho ja esta, e longe do trecho do texto. Fundo
          // translucido da propria linha em vez de caixa com sombra: some quando
          // nao e preciso e nao disputa com o assunto.
          'absolute top-[17px] right-5 flex size-7 toque:size-10 cursor-pointer items-center justify-center rounded-full text-tertiary opacity-0 transition-all duration-[var(--duration-micro)]',
          'hover:bg-selected hover:text-accent focus-visible:opacity-100 focus-visible:ring-2 focus-visible:ring-rail',
          'group-hover/li:opacity-100',
          // Sem hover, o menu fica fixo no canto de baixo, para nao cobrir a hora.
          '[@media(hover:none)]:top-auto [@media(hover:none)]:bottom-3 [@media(hover:none)]:opacity-100',
          selecionada && 'text-white/80 hover:bg-white/15 hover:text-white',
        )}
      >
        <EllipsisVertical size={16} strokeWidth={1.8} aria-hidden="true" />
      </button>
    </>
  );
}
